import {
  ChangeDetectionStrategy,
  Component,
  contentChild,
  input,
  output,
  signal,
  TemplateRef,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  CdkDropListGroup,
} from '@angular/cdk/drag-drop';
import { KanbanColumnComponent } from './kanban-column';

/** Colonne générique : id stable + titre + éléments. */
export interface KanbanColumn<T> {
  id: string;
  title: string;
  items: T[];
}

/** Événement émis lors d'un déplacement de carte entre colonnes. */
export interface KanbanMoveEvent<T> {
  item: T;
  fromColumnId: string;
  toColumnId: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'ui-kanban-board',
  imports: [KanbanColumnComponent, NgTemplateOutlet, CdkDrag, CdkDropList, CdkDropListGroup],
  styleUrl: './kanban.scss',
  templateUrl: './kanban.html',
})
export class KanbanBoardComponent<T extends { id?: string }> {
  /** Colonnes du tableau. */
  readonly columns = input.required<KanbanColumn<T>[]>();

  /** Fonction de rendu du titre des cartes (mode par défaut sans template). */
  readonly cardLabel = input<(item: T) => string>(() => '');

  /** Template optionnel pour personnaliser le contenu des cartes. */
  readonly cardTemplate = contentChild<TemplateRef<unknown>>('kanbanCard');

  /** Émis lorsqu'une carte change de colonne (drag & drop ou clavier). */
  readonly itemMoved = output<KanbanMoveEvent<T>>();

  /** Émis au clic / Entrée sur une carte. */
  readonly itemClick = output<T>();

  protected readonly draggingItemId = signal<string | null>(null);

  /** Drag & drop CDK : dépose d'une carte dans une colonne. */
  protected onDrop(event: CdkDragDrop<string>, targetColumnId: string): void {
    const item = event.item.data as T;
    const fromColumnId = event.previousContainer.data as string;
    if (fromColumnId === targetColumnId) {
      return;
    }
    this.itemMoved.emit({ item, fromColumnId, toColumnId: targetColumnId });
  }

  protected onDragStarted(item: T): void {
    this.draggingItemId.set(item.id ?? null);
  }

  protected onDragEnded(): void {
    this.draggingItemId.set(null);
  }

  /** Accessibilité clavier : ← / → déplacent la carte entre colonnes. */
  protected onCardKeydown(columnId: string, item: T, event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.itemClick.emit(item);
      return;
    }

    const direction = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (direction === 0) {
      return;
    }
    event.preventDefault();

    const columnIds = this.columns().map((column) => column.id);
    const index = columnIds.indexOf(columnId);
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= columnIds.length) {
      return;
    }
    this.itemMoved.emit({ item, fromColumnId: columnId, toColumnId: columnIds[targetIndex] });
  }
}
